// Buat project for a Penyedia: one paket per project, started from its LPSE
// announcement (meeting 28 Oktober).
//   form      Nama project (optional, default "Project baru"), Tempel
//             Pengumuman LPSE (required), Unggah dokumen pemilihan
//             (optional); Buat project creates the project, then Pluto reads
//             the announcement (POST /projects/:id/intake)
//   confirm   the fields Pluto read (./PaketIntake.tsx IntakeConfirm);
//             Simpan saves them (POST /intake/confirm) and opens the project,
//             Lewati opens it without them
//   empty     Pluto found nothing: the project exists, the intake can be
//             repeated from the project page (Tempel Pengumuman LPSE)
//   error     the read failed: the project exists; Coba lagi reads again,
//             Buka project goes on without it
// The dialog is owned by the shared projects provider (shell/Projects.tsx),
// which adds the project to the list and opens it.
import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import * as API from "../api";
import type { PaketIntakeFields, PaketIntakeResult, Project } from "../api";
import { Dialog, Field, Notice } from "../ui/components";
import { Glyph } from "../ui/icons";
import { AnnouncementInput, IntakeConfirm, NOTHING_FOUND, intakeErrorMessage } from "./PaketIntake";

/** The name of a Penyedia's project created without one (as shell/Projects.tsx PAKET_DEFAULT_NAME). */
const DEFAULT_NAME = "Project baru";
const NAME_MAX = 120;

type Phase =
  | { at: "form" }
  | { at: "confirm"; project: Project; result: PaketIntakeResult }
  | { at: "empty"; project: Project }
  | { at: "error"; project: Project; message: string };

export interface PaketCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** A project was created or its paket saved: the shared list takes it. */
  onProject: (project: Project) => void;
  /** Done: close and open the project. */
  onOpen: (projectId: string) => void;
}

function createError(err: unknown): string {
  if (err instanceof API.ApiError && err.status === 409) {
    if (err.body?.code === "project_limit") return "Batas 100 project sudah tercapai. Hapus project yang tidak dipakai, lalu coba lagi.";
    return "Nama project ini sudah dipakai. Pilih nama lain.";
  }
  return API.errorMessage(err);
}

export function PaketCreateDialog({ open, onClose, onProject, onOpen }: PaketCreateDialogProps) {
  const id = useId();
  const formId = `${id}-form`;
  const confirmId = `${id}-confirm`;
  const [phase, setPhase] = useState<Phase>({ at: "form" });
  const [name, setName] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState<"create" | "read" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPhase({ at: "form" });
      setName("");
      setAnnouncement("");
      setFiles([]);
      setBusy(null);
      setError(null);
    }
  }, [open]);

  const read = async (project: Project) => {
    setBusy("read");
    setError(null);
    try {
      const result = await API.intakePaket(project.id, announcement.trim(), files);
      // The files are stored now; a second read must not upload them again.
      if (result.files.length > 0) setFiles([]);
      setPhase(result.proposal && result.fields ? { at: "confirm", project, result } : { at: "empty", project });
    } catch (err) {
      setPhase({ at: "error", project, message: intakeErrorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  const create = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy || !announcement.trim()) return;
    setBusy("create");
    setError(null);
    let project: Project;
    try {
      project = await API.createProject(name.replace(/\s+/g, " ").trim().slice(0, NAME_MAX) || DEFAULT_NAME);
      onProject(project);
    } catch (err) {
      setError(createError(err));
      setBusy(null);
      return;
    }
    await read(project);
  };

  const save = async (project: Project, fields: PaketIntakeFields) => {
    setBusy("save");
    setError(null);
    try {
      const saved = await API.confirmPaketIntake(project.id, fields);
      onProject(saved.project);
      onOpen(project.id);
    } catch (err) {
      setError(intakeErrorMessage(err));
      setBusy(null);
    }
  };

  const close = () => {
    if (busy) return;
    // A project already made is opened rather than left behind unseen.
    if (phase.at !== "form") onOpen(phase.project.id);
    else onClose();
  };

  let body: ReactNode;
  let footer: ReactNode;
  if (phase.at === "form") {
    body = (
      <form id={formId} className="pj-form" onSubmit={(e) => void create(e)} noValidate>
        <Field id={`${id}-name`} label="Nama project" error={error}>
          <div className="control-wrap pj-input">
            <Glyph name="folder" />
            <input
              id={`${id}-name`}
              className="control-icon-start"
              autoComplete="off"
              placeholder={DEFAULT_NAME}
              value={name}
              maxLength={NAME_MAX}
              readOnly={busy !== null}
              aria-describedby={`${id}-name-hint`}
              aria-invalid={error ? true : undefined}
              onChange={(e) => setName(e.currentTarget.value)}
            />
          </div>
        </Field>
        <AnnouncementInput idBase={id} announcement={announcement} onAnnouncement={setAnnouncement} files={files} onFiles={setFiles} disabled={busy !== null} autoFocus />
        <div className="pj-info">
          <Glyph name="infoCircle" size={16} />
          <p>Satu project untuk satu paket. Pluto membaca pengumuman untuk mengisi data paket dan persyaratannya; Anda memeriksanya sebelum disimpan.</p>
        </div>
      </form>
    );
    footer = (
      <button type="submit" form={formId} className="btn btn-primary" disabled={busy !== null || !announcement.trim()} aria-busy={busy !== null || undefined}>
        {busy === "create" ? "Membuat project" : busy === "read" ? "Pluto membaca" : "Buat project"}
      </button>
    );
  } else if (phase.at === "confirm") {
    const { project, result } = phase;
    body = (
      <>
        <IntakeConfirm formId={confirmId} result={result} busy={busy !== null} onSubmit={(f) => void save(project, f)} />
        {error ? (
          <Notice tone="danger" title="Info paket belum tersimpan">
            <p>{error}</p>
          </Notice>
        ) : null}
      </>
    );
    footer = (
      <>
        <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={() => onOpen(project.id)}>
          Lewati
        </button>
        <button type="submit" form={confirmId} className="btn btn-primary" disabled={busy !== null} aria-busy={busy !== null || undefined}>
          {busy === "save" ? "Menyimpan" : "Simpan"}
        </button>
      </>
    );
  } else {
    const { project } = phase;
    body =
      phase.at === "empty" ? (
        <Notice tone="warning" title={NOTHING_FOUND}>
          <p>Project sudah dibuat. Tempel ulang pengumuman yang lengkap dari halaman project (Tempel Pengumuman LPSE), atau isi data lewat Lengkapi Data.</p>
        </Notice>
      ) : (
        <Notice tone="danger" title="Project sudah dibuat, tetapi pengumuman belum terbaca">
          <p>{phase.message}</p>
          <p>Anda dapat mencoba lagi sekarang, atau nanti dari halaman project lewat Tempel Pengumuman LPSE.</p>
        </Notice>
      );
    footer = (
      <>
        {phase.at === "error" ? (
          <button type="button" className="btn btn-secondary" disabled={busy !== null} aria-busy={busy === "read" || undefined} onClick={() => void read(project)}>
            {busy === "read" ? "Pluto membaca" : "Coba lagi"}
          </button>
        ) : null}
        <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => onOpen(project.id)}>
          Buka project
        </button>
      </>
    );
  }

  return (
    <Dialog open={open} title={phase.at === "confirm" ? "Periksa info paket" : "Buat project"} size="md" busy={busy !== null} onClose={close} footer={footer}>
      <div className="pyi">{body}</div>
    </Dialog>
  );
}
